import { apiRequestPaged, buildHeaders, buildURL, handleApiError } from './commons/CommonService.Functions';
import { buildProviderConsumptionsQuery, buildSyncAlarmsQuery } from '../functions/ProviderConsumptions.Functions';

// models
import {
  ProviderConsumption,
  ProviderConsumptionsFilter,
  ProviderConsumptionsPage,
  ProviderConsumptionTotals,
  ProviderSyncAlarm,
  ProviderSyncAlarmsFilter,
  ProviderSyncAlarmsPage,
  ProviderSyncRequestResult,
  ProviderSyncRun,
  ProviderToSettleRequest,
  ProviderToSettleResult,
  RevalueImportedRequest,
  RevalueImportedResult,
} from '../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { ErrorType } from '../models/HttpError';
import { IPagedResult } from '../models/HttpPagedResult';

/**
 * Grilla unificada de consumos del proveedor y su sincronización (spec 006, MedicalProtocols 028).
 * TODAS las llamadas a los endpoints 028 están acá. El gateway mapea /protocols/{everything} →
 * MedicalProtocols API /{everything}. `providerId` es `Provider.id` (int).
 */

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;
const ProvidersURL = () => `${Gateway}protocols/providers`;

/** Corridas que muestra el historial. */
export const SYNC_HISTORY_TAKE = 20;

/** Envoltura con `succeeded: false` (200 con errores de negocio) → ErrorType con los `errors[]` de la API. */
const unwrap = <T>(result: IPagedResult<T> | undefined, status = 200): IPagedResult<T> | undefined => {
  if (result && result.succeeded === false) {
    const error: ErrorType = { ErrorCode: status, ErrorMessage: result.message || 'Operation failed', Errors: result.errors ?? [] };
    throw error;
  }
  return result;
};

const request = async <T>(url: string, token: string, context: string, options: RequestInit = {}): Promise<IPagedResult<T> | undefined> =>
  unwrap(await apiRequestPaged<T>(url, token, context, options));

// ---- URLs ----

export const ProviderConsumptionsURL = (providerId: number, filter: ProviderConsumptionsFilter): string =>
  buildURL(ProvidersURL(), providerId, 'consumptions') + buildProviderConsumptionsQuery(filter, { paged: true });

export const ProviderConsumptionTotalsURL = (providerId: number, filter: ProviderConsumptionsFilter): string =>
  buildURL(ProvidersURL(), providerId, 'consumptions', 'totals') + buildProviderConsumptionsQuery(filter, { paged: false });

export const ProviderSyncURL = (providerId: number): string => buildURL(ProvidersURL(), providerId, 'consumptions', 'sync');

export const LastProviderSyncURL = (providerId: number): string => buildURL(ProvidersURL(), providerId, 'consumptions', 'sync', 'last');

export const ProviderSyncRunsURL = (providerId: number, take = SYNC_HISTORY_TAKE): string => `${ProviderSyncURL(providerId)}?take=${take}`;

export const ProviderSyncRunURL = (runId: string): string => buildURL(ProvidersURL(), 'consumptions', 'sync', runId);

export const ProviderSyncAlarmsURL = (runId: string, filter: ProviderSyncAlarmsFilter): string =>
  buildURL(ProvidersURL(), 'consumptions', 'sync', runId, 'alarms') + buildSyncAlarmsQuery(filter);

// ---- Llamadas ----

/** GET providers/{int}/consumptions?filtros&PageNumber&PageSize&SortBy&SortDirection */
export const GetProviderConsumptions = async (
  token: string,
  providerId: number,
  filter: ProviderConsumptionsFilter
): Promise<ProviderConsumptionsPage> => {
  const result = await request<ProviderConsumption[]>(ProviderConsumptionsURL(providerId, filter), token, 'GetProviderConsumptions');
  const items = result?.data ?? [];
  const pageSize = result?.pageSize ?? filter.pageSize;
  const totalRecords = result?.totalRecords ?? items.length;
  return {
    items,
    totalRecords,
    totalPages: result?.totalPages ?? Math.max(1, Math.ceil(totalRecords / Math.max(1, pageSize))),
    pageNumber: result?.pageNumber ?? filter.pageNumber,
    pageSize,
  };
};

/** GET providers/{int}/consumptions/totals?filtros (ignora paginado y orden). */
export const GetProviderConsumptionTotals = async (
  token: string,
  providerId: number,
  filter: ProviderConsumptionsFilter
): Promise<ProviderConsumptionTotals> => {
  const data = (await request<ProviderConsumptionTotals>(ProviderConsumptionTotalsURL(providerId, filter), token, 'GetProviderConsumptionTotals'))?.data;
  return { totalCount: data?.totalCount ?? 0, alarmCount: data?.alarmCount ?? 0, byCurrency: data?.byCurrency ?? [] };
};

/** GET providers/{int}/consumptions/sync/last → corrida o null (nunca se sincronizó). */
export const GetLastProviderSync = async (token: string, providerId: number): Promise<ProviderSyncRun | null> =>
  (await request<ProviderSyncRun | null>(LastProviderSyncURL(providerId), token, 'GetLastProviderSync'))?.data ?? null;

/** GET providers/{int}/consumptions/sync?take=N → corridas, la más nueva primero. */
export const GetProviderSyncRuns = async (token: string, providerId: number, take = SYNC_HISTORY_TAKE): Promise<ProviderSyncRun[]> =>
  (await request<ProviderSyncRun[]>(ProviderSyncRunsURL(providerId, take), token, 'GetProviderSyncRuns'))?.data ?? [];

/** GET providers/consumptions/sync/{id}. */
export const GetProviderSyncRun = async (token: string, runId: string): Promise<ProviderSyncRun | undefined> =>
  (await request<ProviderSyncRun>(ProviderSyncRunURL(runId), token, 'GetProviderSyncRun'))?.data;

/** GET providers/consumptions/sync/{id}/alarms?reason=&protocolId=&pageNumber=&pageSize= */
export const GetProviderSyncAlarms = async (token: string, runId: string, filter: ProviderSyncAlarmsFilter): Promise<ProviderSyncAlarmsPage> => {
  const result = await request<ProviderSyncAlarm[]>(ProviderSyncAlarmsURL(runId, filter), token, 'GetProviderSyncAlarms');
  const items = result?.data ?? [];
  return {
    items,
    totalRecords: result?.totalRecords ?? items.length,
    pageNumber: result?.pageNumber ?? filter.pageNumber,
    pageSize: result?.pageSize ?? filter.pageSize,
  };
};

/**
 * POST providers/{int}/consumptions/sync (sin cuerpo = general; `{ protocolId }` = un protocolo).
 * 201 → `{ conflict: false, run }`. 409 (ya hay una corrida activa) NO es un error: devuelve
 * `{ conflict: true, activeRunId }` con el id que trae `data`. Otros errores → ErrorType.
 */
export const RequestProviderSync = async (token: string, providerId: number, protocolId?: number): Promise<ProviderSyncRequestResult> => {
  const init: RequestInit = {
    method: 'POST',
    headers: buildHeaders(token),
    body: protocolId === undefined ? undefined : JSON.stringify({ protocolId }),
  };
  let response: Response;
  try {
    response = await fetch(ProviderSyncURL(providerId), init);
  } catch (error: unknown) {
    throw await handleApiError(error, 'RequestProviderSync');
  }
  if (response.status === 409) {
    let body: { data?: unknown } | undefined;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    return { conflict: true, activeRunId: typeof body?.data === 'string' ? body.data : undefined };
  }
  if (!response.ok) throw await handleApiError(response, 'RequestProviderSync');
  const result = unwrap((await response.json()) as IPagedResult<ProviderSyncRun>, response.status);
  return { conflict: false, run: result?.data };
};

/** URL de la revalorización de los importados (ProviderId int). */
export const RevalueImportedURL = (providerId: number): string => buildURL(ProvidersURL(), providerId, 'consumptions', 'revalue-imported');

/**
 * POST providers/{ProviderId:int}/consumptions/revalue-imported { providerFileId? } → { total, byFileValue, byPriceList, unvalued }
 * (spec 008, MP 030): valor del archivo si viene; si no, lista de precios del proveedor; si no, sin valorizar con alarma.
 */
export const RevalueImportedConsumptions = async (token: string, providerId: number, body: RevalueImportedRequest = {}): Promise<RevalueImportedResult> => {
  const payload: RevalueImportedRequest = body.providerFileId ? { providerFileId: body.providerFileId } : {};
  const data = (
    await request<Partial<RevalueImportedResult>>(RevalueImportedURL(providerId), token, 'RevalueImportedConsumptions', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  )?.data;
  return {
    total: Number(data?.total ?? 0),
    byFileValue: Number(data?.byFileValue ?? 0),
    byPriceList: Number(data?.byPriceList ?? 0),
    unvalued: Number(data?.unvalued ?? 0),
  };
};

/** URL del marcado "Por liquidar" (MP 032). */
export const ProviderConsumptionsToSettleURL = (): string => buildURL(ProvidersURL(), 'consumptions', 'to-settle');

/**
 * PATCH providers/consumptions/to-settle (MP 032, spec 010): `{ ids, toSettle }` (hasta 1000) o
 * `{ providerId, filter, toSettle }` (hasta 5000 coincidencias). 200 parcial → `{ updated, rejected (≤ 200), rejectedTotal }`;
 * 400 (forma inválida, más de 1000 ids o filtro con más de 5000) → ErrorType con los `errors[]` de la API.
 */
export const MarkProviderConsumptionsToSettle = async (token: string, body: ProviderToSettleRequest): Promise<ProviderToSettleResult> => {
  const data = (
    await request<Partial<ProviderToSettleResult>>(ProviderConsumptionsToSettleURL(), token, 'MarkProviderConsumptionsToSettle', {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  )?.data;
  const rejected = data?.rejected ?? [];
  return {
    updated: Number(data?.updated ?? 0),
    rejected,
    rejectedTotal: Number(data?.rejectedTotal ?? rejected.length),
  };
};
